import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
} from "react";
import { Lock, LockOpen, MoveHorizontal, MoveVertical } from "lucide-react";
import { SOLVE_PHASES, type SolvePhase } from "@playbookai/types";
import {
  StatusPill,
  Tooltip,
  cn,
  useCanvasViewport,
  type Rect,
} from "@playbookai/ui";
import { PHASE_META, type PhaseMeta } from "./phase-metadata.js";
import {
  PHASE_AREA_KEYBOARD_COARSE_STEP,
  PHASE_AREA_KEYBOARD_STEP,
  PHASE_AREA_SNAP,
  defaultPhaseAreaRect,
  phaseAreaExtent,
  type PhaseAreaLayout,
} from "./phase-areas.js";

/** Called with the phase and its intended next locked state. */
export type PhaseLockToggleHandler = (
  phase: SolvePhase,
  locked: boolean,
) => void;

/** The contents floor of one area, plus what pins it, for the readout copy. */
export interface PhaseAreaFloor {
  readonly w: number;
  readonly h: number;
  readonly widgetCount: number;
}

/** Which edge a gesture is dragging. `w` pushes the areas after this one. */
export type PhaseAreaAxis = "w" | "h";

/**
 * Everything a lane needs to offer a resize gesture. One prop rather than four
 * so the component stays data-blind: it decides WHERE the handles sit and how
 * the gesture feels, never who may resize or where an area stops.
 *
 * `canResize` is asked PER AXIS because the two carry different authority. A
 * height change moves nothing, so it needs only this phase. A width change
 * pushes every later area and carries their widgets, so it needs every one of
 * those phases too — the deny-on-any rule the REST canvas gate already applies
 * to a write that spans phases.
 *
 * `floorFor` is read at gesture start (and again on each keyboard step) so a
 * peer's concurrent widget move is reflected without this component subscribing
 * to the widget map.
 */
export interface PhaseAreaResizeModel {
  /** May this caller drag this edge of this area right now? */
  readonly canResize: (phase: SolvePhase, axis: PhaseAreaAxis) => boolean;
  /** Smallest size the area's own widgets allow. */
  readonly floorFor: (phase: SolvePhase) => PhaseAreaFloor;
  /** Commit a released gesture. The write path re-applies the same bounds. */
  readonly commit: (
    phase: SolvePhase,
    size: { readonly w: number; readonly h: number },
  ) => void;
}

export interface PhaseSwimlanesProps {
  /** Ordered phase prefix currently revealed on the canvas. */
  readonly phases?: readonly SolvePhase[];
  /**
   * Rect per phase, in canvas coordinates. Absent — or missing a phase —
   * renders that phase's historical fixed lane, so a canvas nobody has
   * resized looks exactly as it always did.
   */
  readonly areas?: PhaseAreaLayout | undefined;
  /** Resize affordance + commit. Absent means the areas are not resizable. */
  readonly areaResize?: PhaseAreaResizeModel | undefined;
  /** Phases currently locked (ADR-0019). Drives the per-lane lock affordance. */
  readonly lockedPhases?: readonly SolvePhase[];
  /** Owner/Facilitator may toggle a phase's lock. */
  readonly canManageLocks?: boolean;
  /** Phases whose lock mutation is in flight — each disabled independently so
   *  overlapping (out-of-order) requests can't corrupt the cached lock state. */
  readonly pendingPhases?: ReadonlySet<SolvePhase>;
  readonly onToggleLock?: PhaseLockToggleHandler | undefined;
}

/**
 * The revealed phase areas (Specify → Ensure), drawn as soft-tinted backgrounds
 * plus a rich header card per area: colored letter circle + phase name +
 * subtitle + (top-right) the per-phase lock affordance (ADR-0019). The header
 * content matches the design reference; the metadata lives in
 * `phase-metadata.ts` so the SOLVE phase-strip pill renders the same colors and
 * letters.
 *
 * Each area carries its OWN rect and resizes independently (`phase-areas.ts`);
 * with nothing stored every area falls back to its historical 816px lane.
 *
 * Lives in `<Canvas>`'s `background` slot, which is `pointer-events: none` so
 * panning falls through the header text to the canvas. Only the lock control
 * and the two resize handles re-enable pointer events, so they stay usable
 * without blocking pan/zoom across the rest of the band. That is also why the
 * handles are narrow strips rather than whole edges of the band: everything
 * that opts back in is dead to panning and wheel-zoom.
 *
 * Module-specific by design — lives in `packages/solve-feature/`, not in
 * `packages/ui`'s canvas primitive. VSM ships its own ValueStreamLayout.
 */
export function PhaseSwimlanes({
  phases = SOLVE_PHASES,
  areas,
  areaResize,
  lockedPhases = [],
  canManageLocks = false,
  pendingPhases,
  onToggleLock,
}: PhaseSwimlanesProps = {}): ReactElement {
  const lockedSet = new Set(lockedPhases);
  const rects = new Map<SolvePhase, Rect>(
    phases.map((phase): [SolvePhase, Rect] => [
      phase,
      areas?.get(phase) ?? defaultPhaseAreaRect(phase),
    ]),
  );
  const extent = phaseAreaExtent(rects);
  return (
    <div
      data-geometry="solve-phase-swimlanes"
      data-testid="solve-phase-areas"
      style={{
        position: "relative",
        width: extent.w,
        height: extent.h,
      }}
    >
      {phases.map((phase, i) => (
        <PhaseLane
          key={phase}
          phase={phase}
          rect={rects.get(phase) ?? defaultPhaseAreaRect(phase)}
          isLast={i === phases.length - 1}
          locked={lockedSet.has(phase)}
          pending={pendingPhases?.has(phase) ?? false}
          canManageLocks={canManageLocks}
          onToggleLock={onToggleLock}
          areaResize={areaResize}
        />
      ))}
    </div>
  );
}

interface PhaseLaneProps {
  readonly phase: SolvePhase;
  readonly rect: Rect;
  readonly isLast: boolean;
  readonly locked: boolean;
  readonly pending: boolean;
  readonly canManageLocks: boolean;
  readonly onToggleLock?: PhaseLockToggleHandler | undefined;
  readonly areaResize?: PhaseAreaResizeModel | undefined;
}

/** Grab target in SCREEN px — counter-scaled so it stays that size at any zoom. */
const RESIZE_HANDLE_SCREEN_PX = 14;

interface AreaGesture {
  readonly axis: PhaseAreaAxis;
  readonly pointerId: number;
  readonly startClient: number;
  readonly startSize: number;
  readonly startZoom: number;
  readonly floor: PhaseAreaFloor;
  applied: number;
}

/** What the live readout shows while a gesture is in flight. */
interface ResizePreview {
  readonly axis: PhaseAreaAxis;
  readonly w: number;
  readonly h: number;
  readonly floor: PhaseAreaFloor;
  readonly blocked: boolean;
}

function PhaseLane({
  phase,
  rect,
  isLast,
  locked,
  pending,
  canManageLocks,
  onToggleLock,
  areaResize,
}: PhaseLaneProps): ReactElement {
  const meta: PhaseMeta = PHASE_META[phase];
  const { zoom } = useCanvasViewport();
  const safeZoom = zoom > 0 ? zoom : 1;
  const canResizeWidth = areaResize?.canResize(phase, "w") === true;
  const canResizeHeight = areaResize?.canResize(phase, "h") === true;
  const canResize = canResizeWidth || canResizeHeight;

  const gestureRef = useRef<AreaGesture | null>(null);
  const [preview, setPreview] = useState<ResizePreview | null>(null);
  // Refs so the window listeners registered once below always see the live
  // model and rect without re-binding on every render.
  const modelRef = useRef(areaResize);
  modelRef.current = areaResize;
  const rectRef = useRef(rect);
  rectRef.current = rect;

  useEffect(() => {
    const applyTo = (gesture: AreaGesture, client: number): void => {
      const requested =
        gesture.startSize + (client - gesture.startClient) / gesture.startZoom;
      const snapped = Math.round(requested / PHASE_AREA_SNAP) * PHASE_AREA_SNAP;
      const floor = gesture.axis === "w" ? gesture.floor.w : gesture.floor.h;
      // Growth is unbounded: widening pushes the areas after this one rather
      // than stopping at the neighbour.
      const next = Math.max(floor, snapped);
      if (next === gesture.applied) return;
      gesture.applied = next;
      const base = rectRef.current;
      setPreview({
        axis: gesture.axis,
        w: gesture.axis === "w" ? next : base.w,
        h: gesture.axis === "h" ? next : base.h,
        floor: gesture.floor,
        blocked: snapped < floor,
      });
    };
    const move = (event: PointerEvent): void => {
      const gesture = gestureRef.current;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      applyTo(gesture, gesture.axis === "w" ? event.clientX : event.clientY);
    };
    const finish = (event?: PointerEvent): void => {
      const gesture = gestureRef.current;
      if (!gesture || (event && event.pointerId !== gesture.pointerId)) return;
      if (event) move(event);
      const base = rectRef.current;
      const size = {
        w: gesture.axis === "w" ? gesture.applied : base.w,
        h: gesture.axis === "h" ? gesture.applied : base.h,
      };
      gestureRef.current = null;
      setPreview(null);
      modelRef.current?.commit(phase, size);
    };
    // Escape and pointercancel abandon the gesture: nothing is committed, so
    // the band snaps straight back to the doc's geometry.
    const abandon = (): void => {
      if (!gestureRef.current) return;
      gestureRef.current = null;
      setPreview(null);
    };
    const cancel = (event: PointerEvent): void => {
      const gesture = gestureRef.current;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      abandon();
    };
    const keydown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || !gestureRef.current) return;
      event.preventDefault();
      abandon();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("keydown", keydown);
    return (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", keydown);
    };
  }, [phase]);

  // Edit access can be revoked (or a phase locked) mid-gesture — including a
  // LATER phase, which revokes the width axis alone because the push would carry
  // its widgets. Drop the gesture rather than letting its release commit a write
  // the caller is no longer allowed to make.
  useEffect(() => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    if (gesture.axis === "w" ? canResizeWidth : canResizeHeight) return;
    gestureRef.current = null;
    setPreview(null);
  }, [canResizeWidth, canResizeHeight]);

  const beginResize = (
    axis: PhaseAreaAxis,
    event: ReactPointerEvent<HTMLButtonElement>,
  ): void => {
    const model = modelRef.current;
    if (!model || event.button !== 0 || event.isPrimary === false) return;
    if (!model.canResize(phase, axis)) return;
    event.preventDefault();
    event.stopPropagation();
    const startSize = axis === "w" ? rect.w : rect.h;
    gestureRef.current = {
      axis,
      pointerId: event.pointerId,
      startClient: axis === "w" ? event.clientX : event.clientY,
      startSize,
      startZoom: safeZoom,
      floor: model.floorFor(phase),
      applied: startSize,
    };
    setPreview({
      axis,
      w: rect.w,
      h: rect.h,
      floor: gestureRef.current.floor,
      blocked: false,
    });
  };

  const stepResize = (axis: PhaseAreaAxis, delta: number): void => {
    const model = modelRef.current;
    if (!model || !model.canResize(phase, axis)) return;
    const floor = model.floorFor(phase);
    const requested = (axis === "w" ? rect.w : rect.h) + delta;
    const next = Math.max(axis === "w" ? floor.w : floor.h, requested);
    model.commit(phase, {
      w: axis === "w" ? next : rect.w,
      h: axis === "h" ? next : rect.h,
    });
  };

  const width = preview ? preview.w : rect.w;
  const height = preview ? preview.h : rect.h;
  const handleSize = RESIZE_HANDLE_SCREEN_PX / safeZoom;

  return (
    // keep: canvas-positioned phase lane rect at an arbitrary per-phase accent
    // colour (phase-metadata.ts), sized in canvas units; no Card variant
    // carries a caller-supplied background colour or absolute canvas placement.
    <div
      data-testid={`phase-area-${phase}`}
      data-tour-id={phase === "Specify" ? "solve.phase-frame" : undefined}
      style={{
        position: "absolute",
        left: rect.x,
        top: 0,
        width,
        height,
        backgroundColor: meta.accentBg,
        borderRight: isLast ? "none" : "1px dashed hsl(var(--pb-border) / 0.6)",
      }}
    >
      <PhaseHeaderCard
        phase={phase}
        meta={meta}
        locked={locked}
        pending={pending}
        canManageLocks={canManageLocks}
        onToggleLock={onToggleLock}
      />
      {canResize ? (
        <>
          {/* Bottom edge — height. Moves nothing but this area, so it needs only
              this phase's edit right. Stops short of the right strip so the two
              never contend for the corner. */}
          {canResizeHeight ? (
            <Tooltip
              content={`Drag to resize the ${phase} area height`}
              wrapperClassName="pointer-events-auto absolute bottom-0 left-0 flex touch-none cursor-row-resize items-center justify-center"
              wrapperStyle={{ right: handleSize, height: handleSize }}
            >
              {/* eslint-disable-next-line ds/no-hand-built -- keep: canvas drag handle, zoom-counter-scaled hit target sized in screen px at any canvas zoom; not a standard Button. */}
              <button
                type="button"
                data-testid={`phase-area-resize-height-${phase}`}
                aria-label={`Resize the ${phase} area height (currently ${height} px)`}
                // The grip reveals on `group-focus-visible/area-h`, so the
                // group sits on the focusable button, not the tooltip wrapper.
                className={cn(
                  "group/area-h focus-ring flex h-full w-full items-center justify-center border-b-2 border-transparent transition-colors hover:border-pb-teal/70",
                  preview?.axis === "h" ? "border-pb-teal bg-pb-teal-soft" : "",
                )}
                style={{ borderBottomWidth: 2 / safeZoom }}
                onPointerDown={(event) => beginResize("h", event)}
                onKeyDown={(event) => {
                  if (event.key !== "ArrowUp" && event.key !== "ArrowDown")
                    return;
                  event.preventDefault();
                  event.stopPropagation();
                  stepResize(
                    "h",
                    (event.key === "ArrowDown" ? 1 : -1) *
                      (event.shiftKey
                        ? PHASE_AREA_KEYBOARD_COARSE_STEP
                        : PHASE_AREA_KEYBOARD_STEP),
                  );
                }}
              >
                <ResizeGrip
                  axis="h"
                  active={preview?.axis === "h"}
                  size={handleSize}
                />
              </button>
            </Tooltip>
          ) : null}
          {/* Right edge — width. Widening PUSHES every later area and carries
              their widgets, so it is offered only when this caller may edit all
              of them. */}
          {canResizeWidth ? (
            <Tooltip
              content={`Drag to resize the ${phase} area width`}
              wrapperClassName="pointer-events-auto absolute inset-y-0 right-0 flex touch-none cursor-col-resize items-center justify-center"
              wrapperStyle={{ width: handleSize }}
            >
              {/* eslint-disable-next-line ds/no-hand-built -- keep: canvas drag handle, zoom-counter-scaled hit target sized in screen px at any canvas zoom; not a standard Button. */}
              <button
                type="button"
                data-testid={`phase-area-resize-width-${phase}`}
                aria-label={`Resize the ${phase} area width (currently ${width} px)`}
                // The grip reveals on `group-focus-visible/area-w`, so the
                // group sits on the focusable button, not the tooltip wrapper.
                className={cn(
                  "group/area-w focus-ring flex h-full w-full items-center justify-center border-e-2 border-transparent transition-colors hover:border-pb-teal/70",
                  preview?.axis === "w" ? "border-pb-teal bg-pb-teal-soft" : "",
                )}
                style={{ borderInlineEndWidth: 2 / safeZoom }}
                onPointerDown={(event) => beginResize("w", event)}
                onKeyDown={(event) => {
                  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
                    return;
                  event.preventDefault();
                  event.stopPropagation();
                  stepResize(
                    "w",
                    (event.key === "ArrowRight" ? 1 : -1) *
                      (event.shiftKey
                        ? PHASE_AREA_KEYBOARD_COARSE_STEP
                        : PHASE_AREA_KEYBOARD_STEP),
                  );
                }}
              >
                <ResizeGrip
                  axis="w"
                  active={preview?.axis === "w"}
                  size={handleSize}
                />
              </button>
            </Tooltip>
          ) : null}
          {preview ? (
            <PhaseAreaReadout
              phase={phase}
              preview={preview}
              zoom={safeZoom}
              handleSize={handleSize}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function ResizeGrip({
  axis,
  active,
  size,
}: {
  readonly axis: PhaseAreaAxis;
  readonly active: boolean;
  readonly size: number;
}): ReactElement {
  const Icon = axis === "w" ? MoveHorizontal : MoveVertical;
  return (
    <span
      className={cn(
        "flex items-center justify-center rounded-md border border-pb-teal/50 bg-pb-card text-pb-teal-text shadow-md transition-opacity",
        active
          ? "opacity-100"
          : "opacity-0 group-hover/area-w:opacity-100 group-focus-visible/area-w:opacity-100 group-hover/area-h:opacity-100 group-focus-visible/area-h:opacity-100",
      )}
      style={{ width: size, height: size }}
    >
      <Icon
        aria-hidden="true"
        style={{ width: size * 0.6, height: size * 0.6 }}
      />
    </span>
  );
}

/**
 * The live size, and — when the gesture is pressing past the contents floor —
 * why it stopped. Counter-scaled so it stays legible at any zoom, and anchored
 * to the handle rather than the band's centre (a 3,000px area's centre can be
 * off-screen).
 */
function PhaseAreaReadout({
  phase,
  preview,
  zoom,
  handleSize,
}: {
  readonly phase: SolvePhase;
  readonly preview: ResizePreview;
  readonly zoom: number;
  readonly handleSize: number;
}): ReactElement {
  const value = preview.axis === "w" ? preview.w : preview.h;
  const floor = preview.axis === "w" ? preview.floor.w : preview.floor.h;
  return (
    <div
      data-testid={`phase-area-readout-${phase}`}
      className="pointer-events-none absolute flex items-center gap-2"
      style={
        preview.axis === "w"
          ? {
              top: "50%",
              right: handleSize + 8 / zoom,
              transform: `translateY(-50%) scale(${1 / zoom})`,
              transformOrigin: "right center",
            }
          : {
              left: "50%",
              bottom: handleSize + 8 / zoom,
              transform: `translateX(-50%) scale(${1 / zoom})`,
              transformOrigin: "center bottom",
            }
      }
    >
      <StatusPill variant="elevated" tone="neutral" showDot={false}>
        <span className="pb-tabular font-mono text-pb-text">
          {value.toLocaleString()} px
        </span>
        <span>{preview.axis === "w" ? "wide" : "tall"}</span>
      </StatusPill>
      {preview.blocked ? (
        <StatusPill
          variant="elevated"
          tone="watch"
          showDot={false}
          role="status"
        >
          {preview.floor.widgetCount === 1
            ? `Min ${floor.toLocaleString()} px — 1 widget needs the room`
            : `Min ${floor.toLocaleString()} px — ${preview.floor.widgetCount} widgets need the room`}
        </StatusPill>
      ) : null}
    </div>
  );
}

function PhaseHeaderCard({
  phase,
  meta,
  locked,
  pending,
  canManageLocks,
  onToggleLock,
}: {
  readonly phase: SolvePhase;
  readonly meta: PhaseMeta;
  readonly locked: boolean;
  readonly pending: boolean;
  readonly canManageLocks: boolean;
  readonly onToggleLock?: PhaseLockToggleHandler | undefined;
}): ReactElement {
  return (
    <div
      data-testid={`phase-header-${phase}`}
      style={{
        position: "absolute",
        top: 16,
        left: 20,
        right: 20,
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      {/* Colored letter circle.
          keep: arbitrary per-phase accent colour (phase-metadata.ts) at 36px;
          no Badge/IconTile circle step is 36px and neither takes a
          caller-supplied hex via tone/tint. */}
      <div
        aria-hidden
        style={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: 999,
          backgroundColor: meta.accent,
          color: "hsl(var(--pb-on-accent))",
          fontSize: 16,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {meta.letter}
      </div>
      {/* Name + subtitle */}
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "hsl(var(--pb-text))",
            lineHeight: 1.2,
          }}
        >
          {phase}
        </div>
        <div
          style={{
            fontSize: 11,
            lineHeight: 1.4,
            color: "hsl(var(--pb-text-muted))",
            marginTop: 2,
          }}
        >
          {meta.subtitle}
        </div>
      </div>
      {/* Per-phase lock — top-right. Only this control re-enables pointer events
          (the header card inherits pointer-events:none from the background slot),
          so it's clickable while panning over the header text still falls through. */}
      <PhaseLaneLock
        phase={phase}
        locked={locked}
        pending={pending}
        canManageLocks={canManageLocks}
        onToggleLock={onToggleLock}
      />
    </div>
  );
}

function PhaseLaneLock({
  phase,
  locked,
  pending,
  canManageLocks,
  onToggleLock,
}: {
  readonly phase: SolvePhase;
  readonly locked: boolean;
  readonly pending: boolean;
  readonly canManageLocks: boolean;
  readonly onToggleLock?: PhaseLockToggleHandler | undefined;
}): ReactElement | null {
  if (canManageLocks) {
    return (
      <Tooltip
        content={locked ? `Unlock ${phase}` : `Lock ${phase}`}
        wrapperStyle={{ pointerEvents: "auto", marginLeft: "auto" }}
        wrapperClassName="shrink-0"
      >
        {/* DS keep: no Button variant paints a control-border ring on a card/80 fill with a muted glyph (ghost drops both; secondary/outline brighten the glyph). */}
        {/* eslint-disable-next-line ds/no-hand-built -- DS keep: no Button variant paints this ringed card/80 lock toggle */}
        <button
          type="button"
          data-testid={`phase-lock-toggle-${phase}`}
          disabled={pending}
          aria-label={locked ? `Unlock ${phase} phase` : `Lock ${phase} phase`}
          aria-pressed={locked}
          aria-busy={pending}
          onClick={() => onToggleLock?.(phase, !locked)}
          className={cn(
            "focus-ring inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors",
            "disabled:cursor-not-allowed disabled:opacity-50",
            locked
              ? "border-pb-amber/50 bg-pb-amber/20 text-pb-amber-text"
              : // Unlocked: a subtle ghost OPEN padlock — clearly "lockable", not
                // locked — that brightens on hover/focus and dips on press.
                "hover-quiet border-pb-control-border bg-pb-card/80 text-pb-text-muted active:pb-overlay-paint-strong",
          )}
        >
          {locked ? (
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <LockOpen className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      </Tooltip>
    );
  }
  if (locked) {
    return (
      <span
        data-testid={`phase-locked-${phase}`}
        role="status"
        aria-live="polite"
        aria-label={`${phase} phase locked`}
        title={`${phase} is locked`}
        style={{ pointerEvents: "auto", marginLeft: "auto" }}
        // keep: IconTile's ramp has no 28px step (24/32/40/48 at
        // xs/sm/md/lg), and this read-only indicator matches its
        // interactive sibling's hand-built 28px amber circle above —
        // IconTile size="sm" would grow it to 32px with no approved row
        // for the change.
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-pb-amber/50 bg-pb-amber/20 text-pb-amber-text"
      >
        <Lock className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    );
  }
  return null;
}
